/**
 * The postMessage contract between veriform-desktop (the parent window) and the
 * veriform-web embed bundle (the child frame).
 *
 * This file is duplicated verbatim in both repositories:
 *
 *   veriform-web/src/frontend/embed/embedMessages.ts
 *   veriform-desktop/src/renderer/WebEmbed/embedMessages.ts
 *
 * There is no shared package between them, so the two copies are kept identical
 * by hand -- `diff` them whenever either changes. It has no imports precisely so
 * that staying identical is possible; keep it that way.
 *
 * Why the two sides validate differently
 * --------------------------------------
 * The parent knows exactly who it is talking to: it created the iframe and chose
 * the origin it points at. So it checks both `event.origin` and `event.source`.
 *
 * The child cannot do the same. In the packaged desktop app the renderer is
 * served from `file://`, and a `file://` page's `event.origin` arrives as the
 * string "null" -- there is no parent origin worth allowlisting. The child
 * therefore checks `event.source === window.parent` and nothing else.
 *
 * That asymmetry is acceptable because an embed session holds no cookie session
 * of its own. It has exactly the privileges of the access token the parent hands
 * it, so anything able to forge an `auth` message would need a valid token
 * already, and would gain nothing it did not have.
 */

export const EMBED_READY = 'veriform-embed:ready';
export const EMBED_TOKEN_EXPIRED = 'veriform-embed:token-expired';
export const EMBED_AUTH = 'veriform-embed:auth';

export interface EmbedReadyMessage {
  type: typeof EMBED_READY;
}

export interface EmbedTokenExpiredMessage {
  type: typeof EMBED_TOKEN_EXPIRED;
}

export interface EmbedAuthMessage {
  type: typeof EMBED_AUTH;
  accessToken: string;
}

/** Sent by the embedded page up to the desktop app. */
export type ChildToParentMessage = EmbedReadyMessage | EmbedTokenExpiredMessage;

/** Sent by the desktop app down to the embedded page. */
export type ParentToChildMessage = EmbedAuthMessage;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * The window to accept parent messages from, or null when this page is not
 * framed at all. Top-level pages have `window.parent === window`, so without
 * this guard a page could satisfy its own source check by posting to itself.
 */
export function embedParentWindow(): Window | null {
  if (typeof window === 'undefined') return null;
  return window.parent !== window ? window.parent : null;
}

/**
 * Parent side: validate a message claiming to come from the embedded frame.
 *
 * Returns null for anything that does not qualify. Callers should ignore null
 * silently -- any page receives unrelated postMessage traffic (browser
 * extensions, dev tooling, other frames) as a matter of course, so logging it
 * would be noise rather than signal.
 */
export function readChildMessage(
  event: MessageEvent,
  expected: { origin: string; source: MessageEventSource | null | undefined },
): ChildToParentMessage | null {
  if (!expected.source || event.source !== expected.source) return null;
  if (event.origin !== expected.origin) return null;
  if (!isRecord(event.data)) return null;

  if (event.data.type === EMBED_READY) return { type: EMBED_READY };
  if (event.data.type === EMBED_TOKEN_EXPIRED) return { type: EMBED_TOKEN_EXPIRED };
  return null;
}

/**
 * Child side: validate a message claiming to come from the desktop parent.
 * See the note above on why there is no origin check here.
 */
export function readParentMessage(
  event: MessageEvent,
  expectedSource: MessageEventSource | null,
): ParentToChildMessage | null {
  if (!expectedSource || event.source !== expectedSource) return null;
  if (!isRecord(event.data)) return null;
  if (event.data.type !== EMBED_AUTH) return null;

  const { accessToken } = event.data;
  if (typeof accessToken !== 'string' || accessToken === '') return null;
  return { type: EMBED_AUTH, accessToken };
}

/**
 * Child -> parent. The target origin is '*' because the packaged parent has no
 * addressable origin (see above). These two messages carry no payload, so there
 * is nothing for a wrong recipient to learn.
 */
export function postToParent(message: ChildToParentMessage): void {
  embedParentWindow()?.postMessage(message, '*');
}

/**
 * Parent -> child. The target origin is always explicit: this message carries an
 * access token, and '*' here would hand it to whatever the frame had navigated
 * to in the meantime.
 */
export function postToChild(
  frame: Window | null | undefined,
  message: ParentToChildMessage,
  targetOrigin: string,
): void {
  frame?.postMessage(message, targetOrigin);
}
